/**
 * FASWORLD cart — drawer open/close, line qty updates, live re-render
 */
(() => {
  if (window.__fwCartInit) return;
  window.__fwCartInit = true;

  const rootPath = window.Shopify?.routes?.root || '/';
  const cartUrl = `${rootPath}cart.js`;
  const changeUrl = `${rootPath}cart/change.js`;

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'QAR',
        currencyDisplay: 'code',
      }).format(value);
    } catch {
      return `QAR ${value.toFixed(2)}`;
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const drawer = document.querySelector('[data-fw-cart-drawer]');
  const continueUrl = window.Shopify?.routes?.all_products_collection_url || '/collections/all';
  const cartPageUrl = window.Shopify?.routes?.cart_url || '/cart';

  const readStrings = () => {
    const fallback = {
      yourCartIsEmpty: 'Your cart is empty',
      cartEmptyText: 'Looks like you haven’t added anything yet.',
      continueShopping: 'Continue shopping',
      decreaseQuantity: 'Decrease quantity',
      quantity: 'Quantity',
      increaseQuantity: 'Increase quantity',
      remove: 'Remove',
      updateError: 'Could not update cart',
    };
    const el = document.querySelector('[data-fw-cart-strings]');
    if (!el) return fallback;
    try {
      return { ...fallback, ...JSON.parse(el.textContent) };
    } catch {
      return fallback;
    }
  };

  const strings = readStrings();

  let updating = false;
  let cartClosing = false;
  const CART_MS = 520;

  const openDrawer = () => {
    if (!drawer || typeof drawer.showModal !== 'function' || cartClosing) return;
    if (!drawer.open) {
      drawer.classList.remove('is-closing', 'is-open');
      drawer.showModal();
      document.documentElement.classList.add('fw-cart-open');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          drawer.classList.add('is-open');
        });
      });
      return;
    }
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('fw-cart-open');
  };

  const closeDrawer = () => {
    if (!drawer || !drawer.open || cartClosing) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cartClosing = true;
    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');

    const finish = () => {
      drawer.classList.remove('is-closing');
      if (drawer.open) drawer.close();
      document.documentElement.classList.remove('fw-cart-open');
      cartClosing = false;
    };

    if (reduce) {
      finish();
      return;
    }

    const sheet = drawer.querySelector('.fw-cart-drawer__sheet');
    let done = false;
    const onEnd = (event) => {
      if (!sheet || event.target !== sheet || event.propertyName !== 'transform') return;
      if (done) return;
      done = true;
      sheet.removeEventListener('transitionend', onEnd);
      finish();
    };
    sheet?.addEventListener('transitionend', onEnd);
    window.setTimeout(() => {
      if (done) return;
      done = true;
      sheet?.removeEventListener('transitionend', onEnd);
      finish();
    }, CART_MS + 80);
  };

  const updateCountBubbles = (count) => {
    const safe = Math.max(0, Number(count) || 0);
    document.querySelectorAll('[data-fw-cart-count]').forEach((el) => {
      el.textContent = safe > 99 ? '99+' : String(safe);
    });
    document.querySelectorAll('[data-fw-cart-count-wrap]').forEach((wrap) => {
      wrap.hidden = safe === 0;
      wrap.classList.toggle('visually-hidden', safe === 0);
    });
    document.querySelectorAll('[data-fw-cart-icon]').forEach((icon) => {
      icon.classList.toggle('is-active', safe > 0);
    });
  };

  const lineMarkup = (item) => {
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">`
      : '';
    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="fw-cart-line__meta">${escapeHtml(item.variant_title)}</p>`
        : '';

    return `
      <li class="fw-cart-line" data-fw-cart-line data-key="${escapeHtml(item.key)}">
        <div class="fw-cart-line__media">${image}</div>
        <div class="fw-cart-line__body">
          <a class="fw-cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          <div class="fw-cart-line__row">
            <div class="fw-cart-line__qty">
              <button type="button" data-fw-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="${escapeHtml(strings.decreaseQuantity)}">−</button>
              <input type="number" value="${item.quantity}" min="0" inputmode="numeric" aria-label="${escapeHtml(strings.quantity)}" data-fw-cart-change data-key="${escapeHtml(item.key)}">
              <button type="button" data-fw-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHtml(strings.increaseQuantity)}">+</button>
            </div>
            <span class="fw-cart-line__price">${formatMoney(item.final_line_price)}</span>
          </div>
          <button type="button" class="fw-cart-line__remove" data-fw-cart-change data-key="${escapeHtml(item.key)}" data-quantity="0">${escapeHtml(strings.remove)}</button>
        </div>
      </li>
    `;
  };

  const emptyMarkup = () => `
    <div class="fw-cart-drawer__empty" data-fw-cart-drawer-empty>
      <p class="fw-empty__title">${escapeHtml(strings.yourCartIsEmpty)}</p>
      <p class="fw-empty__text">${escapeHtml(strings.cartEmptyText)}</p>
      <a class="fw-btn" href="${escapeHtml(continueUrl)}" data-fw-cart-close>${escapeHtml(strings.continueShopping)}</a>
    </div>
  `;

  const renderDrawer = (cart) => {
    if (!drawer) return;
    const body = drawer.querySelector('[data-fw-cart-drawer-body]');
    const footer = drawer.querySelector('[data-fw-cart-drawer-footer]');
    if (!body) return;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = emptyMarkup();
      if (footer) footer.hidden = true;
    } else {
      body.innerHTML = `<ul class="fw-cart-lines" role="list" data-fw-cart-lines>${cart.items
        .map(lineMarkup)
        .join('')}</ul>`;
      if (footer) footer.hidden = false;
    }

    drawer.querySelectorAll('[data-fw-cart-subtotal]').forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });
  };

  const renderCartPage = (cart) => {
    const page = document.querySelector('[data-fw-cart-page]');
    if (!page) return;

    const subtotals = page.querySelectorAll('[data-fw-cart-subtotal]');
    subtotals.forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });

    if (!cart.items || cart.items.length === 0) {
      window.location.reload();
      return;
    }

    const lines = page.querySelector('[data-fw-cart-lines]');
    if (lines) {
      lines.innerHTML = cart.items.map(lineMarkup).join('');
    }
  };

  const fetchCart = async () => {
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await fetchCart();
    renderDrawer(cart);
    renderCartPage(cart);
    updateCountBubbles(cart.item_count);
    return cart;
  };

  const changeQuantity = async (key, quantity) => {
    if (!key || updating) return;
    updating = true;
    try {
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, Number(quantity) || 0),
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || strings.updateError);
      }
      document.dispatchEvent(new CustomEvent('fw:cart:updated'));
    } catch (error) {
      console.error(error);
      window.alert(error.message || strings.updateError);
    } finally {
      updating = false;
    }
  };

  const isCartOpenTarget = (target) => {
    if (!(target instanceof Element)) return false;
    if (target.closest('[data-fw-cart-open]')) return true;
    if (target.closest('.fw-cart-trigger')) return true;
    return false;
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-fw-cart-close]') || (target === drawer && event.target === drawer)) {
      closeDrawer();
      return;
    }

    if (isCartOpenTarget(target) && !target.closest('[data-fw-cart-drawer]')) {
      const pageCart = document.querySelector('[data-fw-cart-page]');
      if (pageCart) return;
      event.preventDefault();
      openDrawer();
      return;
    }

    const changeEl = target.closest('[data-fw-cart-change]');
    if (!changeEl) return;

    if (changeEl.tagName === 'INPUT') return;

    event.preventDefault();
    const key = changeEl.getAttribute('data-key');
    const quantity = changeEl.getAttribute('data-quantity');
    changeQuantity(key, quantity);
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-fw-cart-change]')) return;
    const key = target.getAttribute('data-key');
    changeQuantity(key, target.value);
  });

  document.addEventListener('fw:cart:open', openDrawer);
  document.addEventListener('fw:cart:updated', () => {
    refreshCart().catch((error) => console.error(error));
  });

  if (drawer) {
    drawer.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDrawer();
    });
    drawer.addEventListener('close', () => {
      drawer.classList.remove('is-open', 'is-closing');
      document.documentElement.classList.remove('fw-cart-open');
      cartClosing = false;
    });
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) closeDrawer();
    });
  }
})();
